import { Matrix4, Vector3 } from "flavr-math";
import { Annotations, CollisionFacet, ControllerButton, ControllerFacet, CONTROLLER_ROOT_UID, ControllerHand, FacetVisibility, PrimitiveShape, RaycastLine, UpdateFacetOnModelMessage, VisibilityFacet, CollisionShapeLocalTransformFacet, } from "flavr-common";
import { CreateCollisionShapeMessage } from "@messages/selection";
import { modelPool, useExternalMessages, computeLocalMatrixFromWorldMatrix } from "@modules/model";
import { ensureControllerGripSpaceModel } from "./ControllerGripSpace";
import { BUTTON_MAPPING } from "../devices/InputMapping";
const tmpWorldMatrix = new Matrix4();
/**
 * Generates the messages to show a Controller and/or Hand.
 * ControllerView is meant to be used by
 *    - ControllerModule (to display the user's controllers)
 *    - Sessionmodule / ControllerPeer (to display other users' controllers)
 */
export class ControllerView {
    /**
     * @param uid
     * @param isShowingVRController Controls whether the controller shape mesh (e.g. Oculus Touch) is shown.
     * @param info
     * @param hand Left or Right. "UNKNOWN" will throw a console-error... The hand-side might already be included in the parameter "info", but otherwise the assumed side should be entered here.
     * @param baseColor
     * @param selectionInfo
     * @param parentUID
     * @param modelName
     * @param controllerType
     * @param hasTrackedHand Set true when using hand-tracking to suppress the static hand mesh.
     */
    constructor(uid, isShowingVRController, info, hand, baseColor, selectionInfo, parentUID = CONTROLLER_ROOT_UID, modelName = info.name, controllerType = info.type, hasTrackedHand = false) {
        this.uid = uid;
        this.isShowingVRController = isShowingVRController;
        this.info = info;
        this.hand = hand;
        this.baseColor = baseColor;
        this.selectionInfo = selectionInfo;
        this.parentUID = parentUID;
        this.modelName = modelName;
        this.controllerType = controllerType;
        this.showAnnotations = true;
        this.annotationsDirty = true;
        this.hasTrackedHand = false;
        this.buttonText = {};
        this.hasTrackedHand = hasTrackedHand;
        if (this.hand === ControllerHand.Unknown) {
            console.error("The hand-side of the controller should be clear at this point!");
        }
    }
    setButtonDescription(button, description) {
        if (description !== undefined) {
            if (!this.buttonText[button] || this.buttonText[button] !== description) {
                this.buttonText[button] = description;
                this.annotationsDirty = true;
            }
        }
        else if (this.buttonText[button]) {
            delete this.buttonText[button];
            this.annotationsDirty = true;
        }
    }
    showVrController(visible, messages) {
        this.isShowingVRController = visible;
        messages.push(new UpdateFacetOnModelMessage(this.uid, new ControllerFacet(this.controllerType, this.hand, this.isShowingVRController, this.hasTrackedHand, this.baseColor)));
    }
    /**
     * Updates whether the user is using hand-tracking.
     * When true, all legacy static meshes (Hand_A_17 + controller shape) are suppressed.
     */
    setHasTrackedHand(hasTrackedHand, messages) {
        this.hasTrackedHand = hasTrackedHand;
        messages.push(new UpdateFacetOnModelMessage(this.uid, new ControllerFacet(this.controllerType, this.hand, this.isShowingVRController, this.hasTrackedHand, this.baseColor)));
    }
    setShowAnnotations(showAnnotations, messages) {
        if (this.showAnnotations === showAnnotations)
            return;
        this.showAnnotations = showAnnotations;
        if (messages !== undefined) {
            messages.push(new UpdateFacetOnModelMessage(this.uid, new FacetVisibility(Annotations, showAnnotations)));
        }
    }
    showSelection(visible, messages) {
        messages.push(new UpdateFacetOnModelMessage(this.uid, new FacetVisibility(RaycastLine, visible)));
    }
    onButtonPress(messages, buttonIndex) {
        messages.push(new UpdateFacetOnModelMessage(this.uid, new ControllerButton(buttonIndex, true)));
        return messages;
    }
    onButtonRelease(messages, buttonIndex) {
        messages.push(new UpdateFacetOnModelMessage(this.uid, new ControllerButton(buttonIndex, false)));
        return messages;
    }
    createModel(messages) {
        const colliderScale = new Vector3().setScalar(0.1);
        const colliderTransform = new Matrix4().scale(colliderScale);
        const controllerRoot = modelPool.getModelOrThrow(this.parentUID);
        const props = {
            uid: this.uid,
            externalMessages: messages
        };
        const controller = modelPool.createModel(this.modelName, controllerRoot, props);
        ensureControllerGripSpaceModel(controller, this.info, messages);
        const currentRoom = modelPool.getCurrentRoom();
        if (currentRoom !== undefined) {
            controller.setRoom(currentRoom, useExternalMessages(messages));
        }
        messages.push(
        // new UpdateFacetOnModelMessage(controller.getUID(), new ControllerFacet(this.controllerType, this.hand, this.isShowingVRController, this.hasTrackedHand, this.baseColor)),
        new UpdateFacetOnModelMessage(controller.getUID(), new ControllerFacet(this.controllerType, this.hand, this.isShowingVRController, this.hasTrackedHand, this.baseColor)), new UpdateFacetOnModelMessage(controller.getUID(), new VisibilityFacet(true)), new UpdateFacetOnModelMessage(controller.getUID(), new FacetVisibility(Annotations, this.showAnnotations)), new UpdateFacetOnModelMessage(controller.getUID(), new CollisionFacet(PrimitiveShape.Box, false, true)), new CreateCollisionShapeMessage(PrimitiveShape.Box, controller.getUID(), false, true), new UpdateFacetOnModelMessage(controller.getUID(), new CollisionShapeLocalTransformFacet(colliderTransform.toArray())));
        this.annotationsDirty = true;
        if (this.selectionInfo !== undefined) {
            messages.push(new UpdateFacetOnModelMessage(controller.getUID(), new RaycastLine(this.selectionInfo.origin, this.selectionInfo.direction, this.selectionInfo.maxLength, this.selectionInfo.color, true)));
        }
        return messages;
    }
    deleteModel(messages) {
        const model = modelPool.getModel(this.uid);
        if (model !== undefined) {
            modelPool.destroyModel(model, Object.assign({ handleChildren: "destroyRecursive", destroyPermanently: true }, useExternalMessages(messages)));
        }
        return messages;
    }
    update(messages, position, orientation, scale, roomMatrix, transformProps) {
        this.updateAnnotations(messages);
        tmpWorldMatrix.compose(position, orientation, scale);
        (roomMatrix !== undefined) && tmpWorldMatrix.premultiply(roomMatrix);
        const model = modelPool.getModel(this.uid);
        if (model !== undefined) {
            const localMatrix = computeLocalMatrixFromWorldMatrix(model, tmpWorldMatrix);
            model.setTransform(localMatrix, useExternalMessages(messages, transformProps));
        }
        return messages;
    }
    updateAnnotations(messages) {
        if (!this.annotationsDirty)
            return;
        if (!this.showAnnotations)
            return;
        const mapping = BUTTON_MAPPING[this.info.type];
        if (!mapping) {
            return;
        }
        const annotations = [];
        for (const it of this.info.buttons) {
            const type = mapping[it.index];
            if (type === undefined) {
                continue;
            }
            const text = this.buttonText[type];
            if (it.annotation && text) {
                annotations.push({
                    text: text,
                    anchorPoint: it.annotation.anchorPoint,
                    position: it.annotation.position,
                    lineColor: 0x008080,
                    lineWidth: 0.001,
                    textSize: 0.02,
                    maxHeight: 1.5,
                    maxWidth: 6
                });
            }
        }
        if (annotations.length > 0) {
            messages.push(new UpdateFacetOnModelMessage(this.uid, new Annotations(annotations)));
        }
        this.annotationsDirty = false;
    }
}
